import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { User, Mail, KeyRound, CalendarDays, UserPlus, UserCheck, Clock, Swords, Palette, Shield, Trophy, TrendingUp, TrendingDown, Minus, Pencil, Ban, Fingerprint, Users, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Loader, Empty, btn } from "@/components/Page";
import { Banner, Sigil, AllianceSigil } from "@/lib/cosmetics";
import GladiatorCard from "@/components/GladiatorCard";
import { VipBadge } from "@/components/VipBadge";
import { AboutBox } from "@/components/AboutBox";
import { RetiredCard } from "@/pages/Retired";
import { BlockButton } from "@/components/BlockButton";
import Modal from "@/components/Modal";

const PROFILE_RIBBON_ASSETS = {
  grey: "/ribbons/grey.webp",
  green: "/ribbons/green.webp",
  blue: "/ribbons/blue.webp",
  purple: "/ribbons/purple.webp",
  gold: "/ribbons/gold.webp",
};

function ProfileRibbonRail({ ribbons }) {
  if (!ribbons?.length) return <section data-testid="profile-ribbon-placeholder" className="w-full border-l-2 border-zinc-700 bg-black/25 px-3 py-2 text-[11px] uppercase tracking-[0.14em] text-zinc-400 sm:w-auto">No achievement ribbons earned</section>;
  return <section data-testid="profile-ribbon-rail" className="flex w-full flex-wrap gap-2 sm:w-auto sm:max-w-[28rem]">
    {ribbons.map((ribbon) => <div key={ribbon.family} data-testid={`profile-ribbon-${ribbon.family}`} className="flex min-w-0 flex-[1_1_calc(50%-0.25rem)] items-center gap-2 border border-amber-900/40 bg-black/35 px-2 py-1.5 sm:min-w-[12.5rem] sm:flex-1">
      <div className="flex h-12 w-10 shrink-0 items-center justify-center">
        <img data-testid={`profile-ribbon-image-${ribbon.family}`} src={PROFILE_RIBBON_ASSETS[ribbon.color]} alt={`${ribbon.label} ribbon`} className="h-full w-full object-contain" />
      </div>
      <span className="min-w-0 text-[10px] leading-snug text-zinc-200 sm:text-[11px]">{ribbon.label}</span>
    </div>)}
  </section>;
}

function memberSince(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d)) return null;
  const since = d.toLocaleDateString(undefined, { month: "long", year: "numeric" });
  const days = Math.max(0, Math.floor((Date.now() - d.getTime()) / 86400000));
  let dur;
  if (days < 1) dur = "joined today";
  else if (days < 30) dur = `${days} day${days === 1 ? "" : "s"} in the arena`;
  else if (days < 365) { const m = Math.floor(days / 30); dur = `${m} month${m === 1 ? "" : "s"} in the arena`; }
  else { const y = Math.floor(days / 365); const m = Math.floor((days % 365) / 30); dur = `${y} year${y === 1 ? "" : "s"}${m ? ` ${m} mo` : ""} in the arena`; }
  return { since, dur };
}

function lastSeenLabel(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d)) return null;
  const mins = Math.floor((Date.now() - d.getTime()) / 60000);
  if (mins < 2) return "online now";
  if (mins < 60) return `last seen ${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `last seen ${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `last seen ${days} day${days === 1 ? "" : "s"} ago`;
}

function AchievementSummary({ achievements }) {
  const tournamentWins = achievements?.tournament_wins || 0;
  const parAequaTrophies = achievements?.par_aequa_trophies || 0;
  const tournamentBreakdown = achievements?.tournament_win_breakdown || [];
  const [breakdownOpen, setBreakdownOpen] = useState(false);

  return (
    <section data-testid="profile-achievement-summary" className="w-full sm:w-auto border-l-2 border-amber-600/70 bg-zinc-950/60 px-3 py-2.5">
      <div className="flex flex-wrap items-baseline gap-x-5 gap-y-1.5">
        <div className="relative">
          <button
            data-testid="profile-tournament-wins-button"
            type="button"
            onClick={() => setBreakdownOpen((open) => !open)}
            aria-expanded={breakdownOpen}
            aria-controls="profile-tournament-wins-menu"
            className="inline-flex items-center gap-1 whitespace-nowrap text-left transition-colors hover:text-amber-100"
          >
            <span data-testid="profile-tournament-wins-total" className="text-base font-semibold tabular-nums text-amber-300">{tournamentWins}</span>
            <span className="ml-1 text-[11px] text-zinc-400">Tournament Wins</span>
            <ChevronDown size={13} className={`ml-1 text-amber-500 transition-transform duration-150 ${breakdownOpen ? "rotate-180" : ""}`} />
          </button>
          {breakdownOpen && (
            <div id="profile-tournament-wins-menu" data-testid="profile-tournament-wins-menu" className="absolute left-0 top-full z-20 mt-2 w-[min(18rem,calc(100vw-2rem))] border border-amber-700/50 bg-zinc-950 p-3 shadow-xl">
              <div className="text-xs font-cinzel uppercase tracking-[0.16em] text-amber-300">Tournament victories</div>
              {tournamentBreakdown.length ? (
                <div className="mt-2 divide-y divide-zinc-800">
                  {tournamentBreakdown.map((entry) => (
                    <div key={entry.id} data-testid={`profile-tournament-wins-${entry.id}`} className="flex items-center justify-between gap-4 py-1.5 text-sm">
                      <span className="text-zinc-300">{entry.label}</span>
                      <span className="tabular-nums text-amber-200">{entry.wins}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p data-testid="profile-tournament-wins-empty" className="mt-2 text-sm text-zinc-500">No tournament victories recorded.</p>
              )}
            </div>
          )}
        </div>
        {parAequaTrophies > 0 && (
          <div data-testid="profile-par-aequa-trophies" className="whitespace-nowrap">
            <span className="text-base font-semibold tabular-nums text-amber-400">{parAequaTrophies}</span>
            <span className="ml-1.5 text-[11px] text-zinc-400">Par Aequa Titles Included</span>
          </div>
        )}
      </div>
    </section>
  );
}

export default function Profile() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState("");
  const profileRequestRef = useRef(0);
  const [busy, setBusy] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const [curPw, setCurPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [newPw2, setNewPw2] = useState("");
  const [friend, setFriend] = useState(null);   // { status, friendship_id }
  const [removeFriendConfirm, setRemoveFriendConfirm] = useState(false);
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameName, setRenameName] = useState("");

  const loadProfile = useCallback(async () => {
    const requestId = ++profileRequestRef.current;
    setData(null);
    setLoadError("");
    try {
      const response = await api.get(`/users/${userId}/profile`);
      if (requestId === profileRequestRef.current) setData(response.data);
    } catch (error) {
      if (requestId !== profileRequestRef.current) return;
      setData(false);
      if (error?.response?.status !== 404) setLoadError(apiErr(error, "Unable to load this Lanista profile right now."));
    }
  }, [userId]);

  useEffect(() => { loadProfile(); }, [loadProfile]);

  const loadFriend = () => api.get(`/friends/status/${userId}`).then((r) => setFriend(r.data)).catch(() => setFriend(null));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setFriend(null); if (userId) loadFriend(); }, [userId]);

  const doFriend = async (action) => {
    setBusy(true);
    try {
      if (action === "request") { await api.post("/friends/request", { to_user: userId }); toast.success("Friend request sent"); }
      else if (action === "accept") { await api.post(`/friends/${friend.friendship_id}/accept`); toast.success("Friend request accepted"); }
      else if (action === "cancel") { await api.post(`/friends/${friend.friendship_id}/decline`); toast.success("Request cancelled"); }
      else if (action === "remove") { 
        await api.delete(`/friends/${friend.friendship_id}`); 
        toast.success("Removed friend");
        setRemoveFriendConfirm(false);
      }
      loadFriend();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const isMe = user && data && data.user && user.user_id === data.user.user_id;
  const isAdmin = user && user.is_admin;
  
  const [fingerprintModal, setFingerprintModal] = useState(false);
  const [fingerprintData, setFingerprintData] = useState(null);
  const [banFingerprintReason, setBanFingerprintReason] = useState("");

  const toggleShadowban = async () => {
    setBusy(true);
    try {
      const { data: result } = await api.post(`/admin/shadowban/${userId}`);
      toast.success(result.action === "shadowbanned" 
        ? `${result.user_name} has been shadowbanned from arena chat and forum visibility` 
        : `${result.user_name} has been unshadowbanned from arena chat and forum visibility`);
      setData((p) => ({ ...p, user: { ...p.user, arena_shadowbanned: result.arena_shadowbanned } }));
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  
  const loadFingerprintAccounts = async () => {
    if (!data?.user?.fingerprint) {
      toast.error("No device fingerprint recorded for this user (registered before fingerprinting was enabled)");
      return;
    }
    setBusy(true);
    try {
      const { data: result } = await api.get(`/admin/fingerprint/${data.user.fingerprint}/accounts`);
      setFingerprintData(result);
      setFingerprintModal(true);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };
  
  const banFingerprint = async () => {
    if (!banFingerprintReason.trim()) {
      toast.error("Please provide a reason");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/admin/fingerprint/${data.user.fingerprint}/ban`, { reason: banFingerprintReason });
      toast.success(`Fingerprint banned - ${fingerprintData.total_accounts} account(s) affected`);
      setFingerprintModal(false);
      setBanFingerprintReason("");
      await loadFingerprintAccounts();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };
  
  const unbanFingerprint = async () => {
    setBusy(true);
    try {
      await api.delete(`/admin/fingerprint/${data.user.fingerprint}/ban`);
      toast.success("Fingerprint unbanned");
      await loadFingerprintAccounts();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };

  const inviteToAlliance = async () => {
    setBusy(true);
    try {
      await api.post("/alliance/invite", { user_id: userId });
      toast.success(`Invited ${data.user.name} to ${data.alliance_invite?.alliance_name || "your alliance"}`);
      setData((p) => ({ ...p, alliance_invite: { ...p.alliance_invite, pending: true } }));
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const changePassword = async () => {
    if (newPw.length < 6) return toast.error("New password must be at least 6 characters");
    if (newPw !== newPw2) return toast.error("New passwords do not match");
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: curPw, new_password: newPw });
      toast.success("Password updated");
      setPwOpen(false); setCurPw(""); setNewPw(""); setNewPw2("");
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const doRename = async () => {
    if (!renameName.trim()) { toast.error("Name cannot be empty"); return; }
    setBusy(true);
    try {
      const { data: result } = await api.put("/settings/rename-lanista", { name: renameName.trim() });
      toast.success("Lanista name changed!");
      setData((p) => ({ ...p, user: { ...p.user, name: result.name, name_changed: true } }));
      setRenameOpen(false);
      setRenameName("");
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  if (data === null) return <Loader />;
  if (data === false) {
    if (!loadError) return <Empty label="Profile not found." />;
    return (
      <div data-testid="lanista-profile-load-error" className="border border-red-900/50 bg-red-950/20 p-6 text-center">
        <p data-testid="lanista-profile-load-error-message" className="text-sm text-red-200">{loadError}</p>
        <button data-testid="lanista-profile-retry-button" onClick={loadProfile} className="mt-4 border border-amber-700/60 px-4 py-2 text-sm text-amber-200 transition-colors hover:bg-amber-900/30">Try again</button>
      </div>
    );
  }

  return (
    <div>
      {(() => {
        const cos = data.user.cosmetics || {};
        return (
          <div className="mb-6 rounded-sm overflow-hidden border border-amber-900/30" data-testid="profile-banner-header">
            <div className="relative">
              <Banner id={cos.banner} className="absolute inset-0" />
              <div className="relative flex flex-wrap items-center gap-4 p-5 bg-gradient-to-r from-black/80 via-black/55 to-black/25">
                {cos.sigil
                  ? <Sigil id={cos.sigil} size={64} title="Ludus sigil" />
                  : <div className="w-16 h-16 rounded-[26%] bg-zinc-800/80 border border-amber-900/40 flex items-center justify-center font-cinzel text-2xl text-amber-300 shrink-0">{(data.user.name || "?")[0].toUpperCase()}</div>}
                <div className="min-w-0">
                  <h1 data-testid="profile-name" className="font-cinzel text-3xl sm:text-4xl text-amber-100 tracking-wide truncate drop-shadow-lg">{data.user.name}</h1>
                  <p className="text-sm text-zinc-200/90">Lanista · {data.active.length} active, {data.retired.length} retired</p>
                  {data.lanista_rank ? (
                    <>
                      <button data-testid="profile-lanista-rank" onClick={() => navigate("/leaderboard")}
                        title={`Lanista rank ${data.lanista_rank.rank} of ${data.lanista_rank.total} · Rating ${data.lanista_rank.score}`}
                        className="mt-1.5 inline-flex items-center gap-2 bg-black/40 border border-amber-900/40 hover:border-amber-500/70 hover:bg-amber-950/30 rounded-sm px-2.5 py-1 transition-colors cursor-pointer">
                        <Trophy size={14} className="text-amber-400" />
                        <span className="text-sm text-amber-200/95 font-semibold">Lanista Rank #{data.lanista_rank.rank}</span>
                        <span className="text-[11px] uppercase tracking-widest text-zinc-400">of {data.lanista_rank.total}</span>
                        <span className="text-[11px] text-zinc-500">· {data.lanista_rank.score}</span>
                        {data.lanista_rank.trend === "up" && <TrendingUp size={13} className="text-emerald-400" />}
                        {data.lanista_rank.trend === "down" && <TrendingDown size={13} className="text-red-400" />}
                        {data.lanista_rank.trend === "steady" && <Minus size={13} className="text-zinc-500" />}
                      </button>
                      <p className="text-[11px] text-zinc-400 ml-1">
                        <TrendingUp size={11} className="inline text-amber-600" /> Highest: {
                          data.peak_lanista_rank
                            ? <span className="text-amber-400">#{data.peak_lanista_rank}</span>
                            : <span className="text-zinc-500">—</span>
                        }
                      </p>
                    </>
                  ) : (
                    <>
                      <p data-testid="profile-lanista-rank-unranked" className="mt-1.5 text-[11px] uppercase tracking-widest text-zinc-500">Unranked · needs recent ranked fights</p>
                      {data.peak_lanista_rank && (
                        <p className="text-[11px] text-zinc-400 ml-1">
                          <TrendingUp size={11} className="inline text-amber-600" /> Previous Best: <span className="text-amber-400">#{data.peak_lanista_rank}</span>
                        </p>
                      )}
                    </>
                  )}
                  {data.alliance && (
                    <button data-testid="profile-alliance" onClick={() => navigate(`/alliance/${data.alliance.id}`)}
                      title={`View ${data.alliance.name}`}
                      className="mt-1.5 inline-flex items-center gap-2 bg-black/40 border border-amber-900/40 hover:border-amber-500/70 hover:bg-amber-950/30 rounded-sm pl-1.5 pr-2.5 py-1 transition-colors cursor-pointer">
                      <AllianceSigil id={data.alliance.sigil} size={22} title="Alliance sigil" />
                      <span className="text-sm text-amber-200/95 font-medium truncate max-w-[200px]">
                        {data.alliance.name}{data.alliance.tag ? <span className="text-amber-400/70"> [{data.alliance.tag}]</span> : ""}
                      </span>
                      <span className="text-[11px] uppercase tracking-widest text-zinc-400">· {data.alliance.is_leader ? "Dominus" : "Member"}</span>
                    </button>
                  )}
                </div>
                <ProfileRibbonRail ribbons={data.achievements?.ribbons} />
                <div className="ml-auto flex items-center gap-3 flex-wrap">
                  {/* Display approved profile picture in banner */}
                  {data.user.profile_picture_url && (
                    <div className="w-16 h-16 shrink-0">
                      <img 
                        src={data.user.profile_picture_url} 
                        alt={`${data.user.name}'s profile`}
                        className="w-full h-full object-cover rounded-sm border-2 border-amber-600/60 shadow-lg"
                      />
                    </div>
                  )}
                  {isMe && (
                    <>
                      <button data-testid="customize-ludus-btn" className={btn("ghost")} onClick={() => navigate("/customize")}>
                        <span className="flex items-center gap-2"><Palette size={15} /> Customize</span>
                      </button>
                      {!data?.user?.name_changed && (
                        <button 
                          data-testid="rename-lanista-btn" 
                          className={btn("ghost")} 
                          onClick={() => { setRenameOpen(true); setRenameName(data.user.name); }}
                          title="Rename your lanista (one-time only)"
                        >
                          <Pencil size={14} /> Rename
                        </button>
                      )}
                    </>
                  )}
                  {!isMe && (
                    <>
                      {friend?.status === "friends" ? (
                        <button data-testid="unfriend-btn" className={btn("ghost")} onClick={() => setRemoveFriendConfirm(true)} disabled={busy}>
                          <span className="flex items-center gap-2"><UserCheck size={15} className="text-emerald-400" /> Friends</span>
                        </button>
                      ) : friend?.status === "pending_incoming" ? (
                        <button data-testid="accept-friend-btn" className={btn()} onClick={() => doFriend("accept")} disabled={busy}>
                          <span className="flex items-center gap-2"><UserPlus size={15} /> Accept</span>
                        </button>
                      ) : friend?.status === "pending_outgoing" ? (
                        <button data-testid="cancel-friend-btn" className={btn("ghost")} onClick={() => doFriend("cancel")} disabled={busy}>
                          <span className="flex items-center gap-2"><Clock size={15} /> Requested</span>
                        </button>
                      ) : friend?.status === "none" ? (
                        <button data-testid="add-friend-btn" className={btn("ghost")} onClick={() => doFriend("request")} disabled={busy}>
                          <span className="flex items-center gap-2"><UserPlus size={15} /> Add Friend</span>
                        </button>
                      ) : null}
                      <BlockButton userId={data.user.user_id} userName={data.user.name} />
                      <button data-testid="message-manager-btn" className={btn()} onClick={() => navigate("/inbox", { state: { to: data.user.user_id, toName: data.user.name } })}>
                        <span className="flex items-center gap-2"><Mail size={15} /> Message</span>
                      </button>
                      {isAdmin && (
                        <>
                          <button 
                            data-testid="shadowban-toggle-btn" 
                            className={btn("ghost") + (data.user.arena_shadowbanned ? " border-red-700 text-red-400" : "")} 
                            onClick={toggleShadowban} 
                            disabled={busy}
                            title={data.user.arena_shadowbanned ? "Click to unshadowban from arena chat and forum visibility" : "Shadowban from arena chat and forum visibility (admin only)"}
                          >
                            <span className="flex items-center gap-2">
                              <Ban size={15} className={data.user.arena_shadowbanned ? "text-red-400" : ""} /> 
                              {data.user.arena_shadowbanned ? "Shadowbanned" : "Shadowban"}
                            </span>
                          </button>
                          <button
                            data-testid="fingerprint-btn"
                            className={btn("ghost")}
                            onClick={loadFingerprintAccounts}
                            disabled={busy}
                            title="View device fingerprint and linked accounts"
                          >
                            <span className="flex items-center gap-2">
                              <Fingerprint size={15} />
                              Device ID
                            </span>
                          </button>
                        </>
                      )}
                      {data.alliance_invite?.can_invite && (
                        data.alliance_invite.pending ? (
                          <button data-testid="alliance-invited-btn" className={btn("ghost")} disabled>
                            <span className="flex items-center gap-2"><Shield size={15} className="text-emerald-400" /> Invited</span>
                          </button>
                        ) : (
                          <button data-testid="invite-alliance-btn" className={btn("ghost")} disabled={busy || data.alliance_invite.full}
                            title={data.alliance_invite.full ? "Your alliance is full" : `Invite to ${data.alliance_invite.alliance_name}`}
                            onClick={inviteToAlliance}>
                            <span className="flex items-center gap-2"><Shield size={15} /> {data.alliance_invite.full ? "Alliance Full" : "Invite to Alliance"}</span>
                          </button>
                        )
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {data.user.vip && <div className="-mt-3 mb-6" data-testid="profile-vip"><VipBadge /></div>}

      {(() => {
        const m = memberSince(data.user.created_at);
        if (!m) return null;
        const seen = lastSeenLabel(data.user.last_seen);
        return (
          <div data-testid="profile-member-since" className="-mt-2 mb-6 inline-flex items-center gap-2 text-sm text-zinc-400 border border-amber-900/30 bg-zinc-950/60 px-3 py-1.5 rounded-sm">
            <CalendarDays size={14} className="text-amber-500/80" />
            <span>Lanista since <span className="text-amber-200">{m.since}</span></span>
            <span className="text-zinc-600">·</span>
            <span className="text-zinc-500">{m.dur}</span>
            {seen && <>
              <span className="text-zinc-600">·</span>
              <span data-testid="profile-last-seen" className={seen === "online now" ? "text-emerald-400" : "text-zinc-500"}>{seen}</span>
            </>}
            {data.friend_count != null && <>
              <span className="text-zinc-600">·</span>
              <span data-testid="profile-friend-count" className="text-zinc-500">{data.friend_count} {data.friend_count === 1 ? "Friend" : "Friends"}</span>
            </>}
          </div>
        );
      })()}

      <div className="-mt-2 mb-6 flex flex-wrap items-center gap-3">
        <AchievementSummary achievements={data.achievements} />
        {data.record && (
          <div data-testid="profile-record" className="inline-flex items-center gap-2 text-sm border border-amber-900/30 bg-zinc-950/60 px-3 py-1.5 rounded-sm">
            <Swords size={14} className="text-amber-500/80" />
            <span className="text-zinc-400">Manager record</span>
            <span className="text-emerald-400 font-semibold tabular-nums">{data.record.wins}W</span>
            <span className="text-zinc-600">/</span>
            <span className="text-red-400 font-semibold tabular-nums">{data.record.losses}L</span>
          </div>
        )}
        {!isMe && data.head_to_head && (
          <div data-testid="profile-head-to-head" className="inline-flex items-center gap-2 text-sm border border-amber-800/40 bg-amber-950/20 px-3 py-1.5 rounded-sm">
            <span className="text-zinc-400">Your record vs {data.user.name}</span>
            <span className="text-emerald-400 font-semibold tabular-nums">{data.head_to_head.wins}W</span>
            <span className="text-zinc-600">/</span>
            <span className="text-red-400 font-semibold tabular-nums">{data.head_to_head.losses}L</span>
          </div>
        )}
      </div>

      {isMe && (
        <div className="mb-8 -mt-2 max-w-md" data-testid="account-security">
          {!pwOpen ? (
            <button data-testid="change-password-btn" className={btn("ghost")} onClick={() => setPwOpen(true)}>
              <span className="flex items-center gap-2"><KeyRound size={15} /> Change Password</span>
            </button>
          ) : (
            <div className="bg-zinc-950 border border-amber-900/30 p-4 space-y-3">
              <div className="flex items-center gap-2 text-amber-300 font-cinzel uppercase tracking-wide text-sm"><KeyRound size={15} /> Change Password</div>
              <input data-testid="current-password-input" type="password" placeholder="Current password" value={curPw} onChange={(e) => setCurPw(e.target.value)}
                className="w-full bg-zinc-900 border border-zinc-800 px-3 py-2.5 text-zinc-100 focus:border-amber-600 outline-none" />
              <input data-testid="new-password-input" type="password" placeholder="New password (min 6 chars)" value={newPw} onChange={(e) => setNewPw(e.target.value)}
                className="w-full bg-zinc-900 border border-zinc-800 px-3 py-2.5 text-zinc-100 focus:border-amber-600 outline-none" />
              <input data-testid="confirm-password-input" type="password" placeholder="Confirm new password" value={newPw2} onChange={(e) => setNewPw2(e.target.value)}
                className="w-full bg-zinc-900 border border-zinc-800 px-3 py-2.5 text-zinc-100 focus:border-amber-600 outline-none" />
              <div className="flex gap-2">
                <button data-testid="save-password-btn" disabled={busy} className={btn() + " flex-1"} onClick={changePassword}>Update Password</button>
                <button className={btn("ghost") + " px-4"} onClick={() => { setPwOpen(false); setCurPw(""); setNewPw(""); setNewPw2(""); }}>Cancel</button>
              </div>
            </div>
          )}
        </div>
      )}

      <AboutBox about={data.user.about} canEdit={isMe} endpoint="/settings/about"
        onSaved={(about) => setData((p) => ({ ...p, user: { ...p.user, about } }))}
        placeholder="Tell other lanistae about yourself…" testid="manager-about" maxLength={500} rows={5} />

      <h2 className="font-cinzel text-amber-300 uppercase tracking-wide text-sm mb-3">Active Gladiators</h2>
      {data.active.length === 0 ? <Empty label="No active gladiators." /> : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
          {data.active.map((g) => <GladiatorCard key={g.id} g={g} onClick={() => navigate(`/gladiator/${g.id}`)} showStats={isMe} />)}
        </div>
      )}

      {/* Remove Friend Confirmation Modal */}
      <Modal open={removeFriendConfirm} onClose={() => setRemoveFriendConfirm(false)} title="Remove Friend">
        <div className="space-y-4">
          <p className="text-zinc-300">
            Are you sure you want to remove <span className="text-amber-300 font-cinzel">{data.user.name}</span> from your friends list?
          </p>
          <p className="text-sm text-zinc-500">
            You can send them a friend request again if you change your mind.
          </p>
          <div className="flex gap-3 pt-2">
            <button 
              data-testid="confirm-remove-friend-btn"
              className={btn() + " flex-1 bg-red-900 border-red-700 hover:bg-red-800"} 
              onClick={() => doFriend("remove")} 
              disabled={busy}
            >
              Remove Friend
            </button>
            <button 
              data-testid="cancel-remove-friend-btn"
              className={btn("ghost") + " flex-1"} 
              onClick={() => setRemoveFriendConfirm(false)}
              disabled={busy}
            >
              Cancel
            </button>
          </div>
        </div>
      </Modal>

      {/* Rename lanista modal */}
      <Modal open={renameOpen} onClose={() => setRenameOpen(false)} title="Rename Lanista">
        <div className="space-y-4">
          <div className="text-sm text-zinc-400">
            <p className="mb-2">Choose a new name for your lanista. This is a <span className="text-amber-400 font-semibold">one-time change</span> and cannot be undone.</p>
            {data?.user.name_changed ? (
              <p className="text-amber-500 text-xs">⚠️ You have already used your one-time name change.</p>
            ) : (
              <p className="text-zinc-500 text-xs">• Name must be 2-40 characters<br />• Must be unique</p>
            )}
          </div>
          <div>
            <label className="text-xs text-zinc-400">New Lanista Name</label>
            <input 
              type="text" 
              data-testid="rename-input" 
              value={renameName} 
              onChange={(e) => setRenameName(e.target.value)} 
              maxLength={40}
              placeholder="Enter new name..."
              className="w-full px-3 py-2 bg-zinc-900 border border-zinc-700 text-zinc-100 text-sm rounded-sm"
            />
          </div>
          <button 
            data-testid="rename-confirm-btn" 
            disabled={busy || !renameName.trim() || data?.user.name_changed} 
            onClick={doRename} 
            className={btn() + " w-full"}
          >
            {busy ? "Renaming..." : "Confirm Rename"}
          </button>
        </div>
      </Modal>

      {/* Fingerprint Management Modal (Admin only) */}
      {fingerprintModal && fingerprintData && (
        <Modal open={fingerprintModal} onClose={() => { setFingerprintModal(false); setBanFingerprintReason(""); }} title="Device Fingerprint">
          <div className="space-y-4">
            <div className="p-3 bg-zinc-900/60 border border-zinc-800 rounded-sm">
              <div className="flex items-center gap-2 mb-2">
                <Fingerprint size={16} className="text-amber-400" />
                <span className="text-sm font-semibold text-amber-300">Device Fingerprint ID</span>
              </div>
              <code className="text-xs text-zinc-400 break-all font-mono bg-zinc-950 px-2 py-1 rounded block">
                {fingerprintData.fingerprint}
              </code>
            </div>

            {fingerprintData.is_banned && fingerprintData.banned_info && (
              <div className="p-3 bg-red-950/20 border border-red-900/50 rounded-sm">
                <p className="text-sm text-red-300 font-semibold mb-1">🚫 Fingerprint Banned</p>
                <p className="text-xs text-red-400 mb-1">Reason: {fingerprintData.banned_info.reason}</p>
                <p className="text-xs text-zinc-500">
                  Banned on {new Date(fingerprintData.banned_info.banned_at).toLocaleString()}
                </p>
                <button
                  onClick={unbanFingerprint}
                  disabled={busy}
                  className="mt-2 text-xs px-3 py-1.5 bg-emerald-900 border border-emerald-700 text-emerald-100 rounded-sm hover:bg-emerald-800"
                >
                  {busy ? "Unbanning..." : "Unban Fingerprint"}
                </button>
              </div>
            )}

            <div>
              <div className="flex items-center gap-2 mb-2">
                <Users size={16} className="text-zinc-400" />
                <span className="text-sm font-semibold text-zinc-300">
                  Accounts Using This Device ({fingerprintData.total_accounts})
                </span>
              </div>
              
              {fingerprintData.accounts.length === 0 ? (
                <p className="text-sm text-zinc-500">No accounts found</p>
              ) : (
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {fingerprintData.accounts.map((account) => (
                    <div key={account.user_id} className="p-2 bg-zinc-900/40 border border-zinc-800 rounded-sm">
                      <div className="flex justify-between items-center">
                        <div>
                          <p className="text-sm text-zinc-200 font-medium">{account.name}</p>
                          <p className="text-xs text-zinc-500">{account.email}</p>
                        </div>
                        <button
                          onClick={() => navigate(`/profile/${account.user_id}`)}
                          className="text-xs text-amber-400 hover:text-amber-300 underline"
                        >
                          View
                        </button>
                      </div>
                      <p className="text-xs text-zinc-600 mt-1">
                        Joined {new Date(account.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {!fingerprintData.is_banned && (
              <div className="pt-3 border-t border-zinc-800">
                <p className="text-sm text-zinc-400 mb-2">Ban this device fingerprint?</p>
                <p className="text-xs text-zinc-500 mb-3">
                  This will prevent new account creation and logins from this device. All {fingerprintData.total_accounts} account(s) will be flagged.
                </p>
                <textarea
                  value={banFingerprintReason}
                  onChange={(e) => setBanFingerprintReason(e.target.value)}
                  placeholder="Reason for ban (e.g., ban evasion, multi-accounting)..."
                  maxLength={500}
                  rows={3}
                  className="w-full px-3 py-2 bg-zinc-900 border border-zinc-700 text-zinc-100 text-sm rounded-sm resize-none mb-2"
                />
                <button
                  onClick={banFingerprint}
                  disabled={busy || !banFingerprintReason.trim()}
                  className="w-full px-4 py-2 bg-red-900 border border-red-700 text-zinc-100 text-sm rounded-sm hover:bg-red-800 disabled:opacity-40"
                >
                  {busy ? "Banning..." : "Ban Fingerprint"}
                </button>
              </div>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
}
